/* Blastoys scroll animations.
   Mirrors the framer-motion whileInView behaviour from the Lovable build:
   elements fade + rise as they enter the viewport, with a stagger inside grids. */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function reveal(root) {
    var targets = (root || document).querySelectorAll('.bt-reveal, .bt-zoom');
    if (!targets.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('bt-in'); });
      return;
    }

    // FAILSAFE. .bt-reveal starts at opacity:0, so if the observer never fires the
    // content is invisible FOREVER — the worst failure this file can produce.
    // IntersectionObserver does not compute in a background tab, and a page can be
    // restored from bfcache or opened in one. Belt and braces:
    //   1. force everything visible after a short grace period, and
    //   2. re-run when the tab becomes visible.
    // Both are idempotent: adding bt-in twice is harmless, and anything already
    // revealed by the observer is untouched.
    setTimeout(function () {
      targets.forEach(function (el) { el.classList.add('bt-in'); });
    }, 1500);

    if (document.visibilityState === 'hidden') {
      document.addEventListener('visibilitychange', function onVis() {
        if (document.visibilityState !== 'visible') return;
        document.removeEventListener('visibilitychange', onVis);
        targets.forEach(function (el) { el.classList.add('bt-in'); });
      });
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('bt-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });

    targets.forEach(function (el) {
      // Stagger siblings so grids cascade rather than popping in together.
      if (!el.style.getPropertyValue('--bt-delay') && el.parentElement) {
        var sibs = Array.prototype.filter.call(el.parentElement.children, function (c) {
          return c.classList.contains('bt-reveal') || c.classList.contains('bt-zoom');
        });
        var i = sibs.indexOf(el);
        if (i > 0) el.style.setProperty('--bt-delay', Math.min(i * 70, 420) + 'ms');
      }
      io.observe(el);
    });
  }

  function marquee(root) {
    // Duplicate the track contents so the -50% keyframe loops seamlessly.
    (root || document).querySelectorAll('.bt-marquee-track:not([data-bt-cloned])').forEach(function (track) {
      track.setAttribute('data-bt-cloned', '');
      track.innerHTML += track.innerHTML;
    });
  }

  function heroRotator(root) {
    // Port of HeroVideos: crossfade a playlist, lazy-load the next clip only,
    // trim the list on mobile to save data, and pause when off-screen.
    (root || document).querySelectorAll('[data-bt-hero-rotator]:not([data-bt-init])').forEach(function (wrap) {
      wrap.setAttribute('data-bt-init', '');
      var clips = Array.prototype.slice.call(wrap.querySelectorAll('.bt-hero-clip'));
      if (!clips.length) return;

      var isMobile = window.matchMedia('(max-width: 768px)').matches;
      var limit = parseInt(wrap.getAttribute('data-mobile-limit'), 10) || 3;
      if (isMobile && clips.length > limit) {
        clips.slice(limit).forEach(function (c) { c.remove(); });
        clips = clips.slice(0, limit);
      }

      var first = clips[0];
      if (first && !first.src && first.dataset.src) first.src = first.dataset.src;
      var play = function (v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); };
      if (first) play(first);
      if (clips.length < 2) return;

      var i = 0, timer = null;
      var interval = parseInt(wrap.getAttribute('data-interval'), 10) || 7000;

      function preload(n) {
        var v = clips[n];
        if (v && !v.src && v.dataset.src) { v.src = v.dataset.src; v.load(); }
      }
      preload(1);

      function advance() {
        var cur = clips[i];
        i = (i + 1) % clips.length;
        var nxt = clips[i];
        preload(i);
        nxt.currentTime = 0;
        play(nxt);
        nxt.classList.add('bt-hero-clip--on');
        cur.classList.remove('bt-hero-clip--on');
        // Stop the outgoing clip once it has faded out.
        setTimeout(function () { if (!cur.classList.contains('bt-hero-clip--on')) cur.pause(); }, 1200);
        preload((i + 1) % clips.length);
      }

      function start() { if (!timer) timer = setInterval(advance, interval); }
      function stop() { clearInterval(timer); timer = null; }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (es) {
          es.forEach(function (e) {
            if (e.isIntersecting) { play(clips[i]); start(); }
            else { stop(); clips.forEach(function (c) { c.pause(); }); }
          });
        }, { threshold: 0.05 }).observe(wrap);
      } else {
        start();
      }

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { stop(); clips.forEach(function (c) { c.pause(); }); }
        else { play(clips[i]); start(); }
      });
    });
  }

  function washRotator(root) {
    // Port of RotatingVideo: background playlist behind a section, 1500ms opacity
    // crossfade, 9s default dwell, trimmed to 2 clips on mobile, idle when off-screen.
    (root || document).querySelectorAll('[data-bt-wash]:not([data-bt-init])').forEach(function (wrap) {
      wrap.setAttribute('data-bt-init', '');
      var vids = Array.prototype.slice.call(wrap.querySelectorAll('video'));
      if (!vids.length) return;

      var limit = parseInt(wrap.getAttribute('data-mobile-limit'), 10) || 2;
      if (window.matchMedia('(max-width: 768px)').matches && vids.length > limit) {
        vids.slice(limit).forEach(function (v) { v.remove(); });
        vids = vids.slice(0, limit);
      }

      var play = function (v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); };
      var load = function (n) {
        var v = vids[n];
        if (v && !v.src && v.dataset.src) { v.src = v.dataset.src; v.load(); }
      };

      load(0);
      vids[0].classList.add('bt-wash--on');

      var i = 0, timer = null;
      var interval = parseInt(wrap.getAttribute('data-interval'), 10) || 9000;

      function advance() {
        var cur = vids[i];
        i = (i + 1) % vids.length;
        var nxt = vids[i];
        load(i);
        nxt.currentTime = 0;
        play(nxt);
        nxt.classList.add('bt-wash--on');
        cur.classList.remove('bt-wash--on');
        setTimeout(function () { if (!cur.classList.contains('bt-wash--on')) cur.pause(); }, 1600);
      }

      function start() { if (!timer && vids.length > 1) timer = setInterval(advance, interval); }
      function stop() { clearInterval(timer); timer = null; }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (es) {
          es.forEach(function (e) {
            if (e.isIntersecting) { load(1); play(vids[i]); start(); }
            else { stop(); vids.forEach(function (v) { v.pause(); }); }
          });
        }, { threshold: 0.02, rootMargin: '200px' }).observe(wrap);
      } else {
        play(vids[0]); start();
      }

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { stop(); vids.forEach(function (v) { v.pause(); }); }
        else { play(vids[i]); start(); }
      });
    });
  }

  // reveal() runs FIRST and each step is isolated: previously reveal() was last, so
  // a throw in any rotator (a malformed clip URL, a missing element) would silently
  // leave every .bt-reveal element stuck at opacity:0 and the page looking blank.
  function init(root) {
    [reveal, marquee, heroRotator, washRotator].forEach(function (fn) {
      try { fn(root); } catch (e) { /* one broken feature must not hide the page */ }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Re-run when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
